/**
 * React Hook for Calculation Form Validation
 *
 * Provides validation logic and state management for insurance calculation forms.
 * Integrates with react-hook-form for seamless validation experience.
 */

import { useState, useCallback, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  validateCalculationRequest,
  ValidationError,
  ValidationResult,
  getFieldErrorMessage,
  hasFieldError,
} from "utils/validation/calculationValidation";

export interface UseCalculationValidationProps {
  /**
   * React Hook Form methods
   */
  formMethods?: UseFormReturn<any>;

  /**
   * Callback when validation state changes
   */
  onValidationChange?: (isValid: boolean, errors: ValidationError[]) => void;
}

export interface UseCalculationValidationReturn {
  /**
   * All current validation errors
   */
  validationErrors: ValidationError[];

  /**
   * Whether the form is currently valid
   */
  isValid: boolean;

  /**
   * Validate the entire calculation request
   */
  validateRequest: (request: any) => ValidationResult;

  /**
   * Get error message for a specific field
   */
  getError: (fieldPath: string) => string | undefined;

  /**
   * Check if a field has an error
   */
  hasError: (fieldPath: string) => boolean;

  /**
   * Get all errors for a specific field
   */
  getFieldErrors: (fieldPath: string) => ValidationError[];

  /**
   * Clear all validation errors
   */
  clearErrors: () => void;

  /**
   * Set validation errors manually
   */
  setErrors: (errors: ValidationError[]) => void;

  /**
   * Scroll to first error field
   */
  scrollToFirstError: () => void;
}

/**
 * Hook for managing calculation form validation
 */
export const useCalculationValidation = (
  props?: UseCalculationValidationProps
): UseCalculationValidationReturn => {
  const { onValidationChange } = props || {};

  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [isValid, setIsValid] = useState<boolean>(true);

  // Notify parent component when validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid, validationErrors);
    }
  }, [isValid, validationErrors, onValidationChange]);

  /**
   * Maps backend field paths to frontend field paths
   */
  const mapBackendFieldToFrontend = useCallback(
    (backendField: string): string => {
      const fieldMapping: Record<string, string> = {
        // Vehicle fields
        "Vehicle.RegistrationCertificateNumber":
          "vehicleData.technicalPassportNumber",
        "Vehicle.LicensePlate": "vehicleData.licensePlateNumber",
        "Vehicle.Vin": "vehicleData.vin",
        "Vehicle.Brand": "vehicleData.vehicleMake",
        "Vehicle.Model": "vehicleData.vehicleModel",
        "Vehicle.CubicCapacityCcm": "vehicleData.engineCapacity",
        "Vehicle.PowerKw": "vehicleData.power",
        "Vehicle.MaximumGrossWeight": "vehicleData.maximumGrossWeight",
        "Vehicle.ManufactureDate": "vehicleData.productionYear",
        "Vehicle.Owner.Code": "vehicleData.ownerCode",
        "Vehicle.Owner.Name": "vehicleData.ownerName",
        // Client fields
        "Client.Code": "clientData.clientCode",
        "Client.Name": "clientData.clientName",
        "Client.FirstName": "clientData.clientName",
        "Client.LastName": "clientData.clientName",
        "Client.Age": "clientData.age",
        "Client.Experience": "clientData.drivingExperience",
        "Client.Address.Email": "clientData.clientEmail",
        "Client.Address.Phone": "clientData.phone",
        "Client.Address.AddressString": "clientData.address",
        // Insurance fields
        "Insurance.StartDate": "proposalData.policyStartDate",
        "Insurance.DriverLicenceDate": "clientData.licenseDate",
        "Insurance.CoverageTerritory": "clientData.territoryOfValidity",
      };

      return fieldMapping[backendField] || backendField;
    },
    []
  );

  /**
   * Validate complete calculation request
   */
  const validateRequest = useCallback((request: any): ValidationResult => {
    const result = validateCalculationRequest(request);
    setValidationErrors(result.errors);
    setIsValid(result.isValid);
    return result;
  }, []);

  /**
   * Get error message for a specific field
   * Supports both frontend and backend field paths
   */
  const getError = useCallback(
    (fieldPath: string): string | undefined => {
      // Try direct match first (for frontend field paths)
      let error = getFieldErrorMessage(validationErrors, fieldPath);

      // If not found, try to find by mapping frontend to backend
      if (!error) {
        // Check if any error's backend field maps to this frontend field
        const matchingError = validationErrors.find(
          (err) => mapBackendFieldToFrontend(err.field) === fieldPath
        );
        if (matchingError) {
          error = matchingError.message;
        }
      }

      return error;
    },
    [validationErrors, mapBackendFieldToFrontend]
  );

  /**
   * Check if a field has an error
   */
  const hasError = useCallback(
    (fieldPath: string): boolean => {
      return hasFieldError(validationErrors, fieldPath);
    },
    [validationErrors]
  );

  /**
   * Get all errors for a specific field
   */
  const getFieldErrors = useCallback(
    (fieldPath: string): ValidationError[] => {
      return validationErrors.filter((error) => error.field === fieldPath);
    },
    [validationErrors]
  );

  /**
   * Clear all validation errors
   */
  const clearErrors = useCallback(() => {
    setValidationErrors([]);
    setIsValid(true);
  }, []);

  /**
   * Set validation errors manually
   */
  const setErrors = useCallback((errors: ValidationError[]) => {
    setValidationErrors(errors);
    setIsValid(errors.length === 0);
  }, []);

  /**
   * Scroll to first error field
   */
  const scrollToFirstError = useCallback(() => {
    if (validationErrors.length === 0) return;

    const firstError = validationErrors[0];
    if (!firstError) return;

    // Try to find the field in the DOM
    // Assuming fields have name attributes matching the field path
    const fieldName = firstError.field.replace(/\./g, "\\.");
    const selectors = [
      `[name="${fieldName}"]`,
      `[data-field="${fieldName}"]`,
      `#${fieldName.replace(/\./g, "-")}`,
    ];

    for (const selector of selectors) {
      try {
        const element = document.querySelector(selector);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          // Try to focus if it's an input element
          if (element instanceof HTMLElement) {
            element.focus();
          }
          break;
        }
      } catch (error) {
        // Ignore selector errors
        console.debug("Error finding field element:", selector, error);
      }
    }
  }, [validationErrors]);

  return {
    validationErrors,
    isValid,
    validateRequest,
    getError,
    hasError,
    getFieldErrors,
    clearErrors,
    setErrors,
    scrollToFirstError,
  };
};
